import { useMemo, useContext, useState, useCallback } from 'react';
import { useSlate } from 'slate-react';
import { Editor as SlateEditor } from 'slate';
import { TaxonomyChild, TagWithMetadata } from 'app/api/tag-queries/types';
import { useCreateTag, useContinueTag, useUpdateTag } from 'app/api/tag-queries/tags';
import { useConcept } from 'app/api/tag-queries/taxonomy';
import { useDocumentTaxonomyConfig } from 'app/api/tag-queries/document-taxonomy-config';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { createAndInsertTag, getSelectionElementId } from 'app/slate/extensions/tag';
import { insertExclude } from 'app/slate/extensions/tag/utils/exclusion';
import { insertFootnote } from 'app/slate/extensions/tag/utils/footnote';
import { useCreateFootnote } from 'app/api/tag-queries/footnotes';
import { v4 as uuidv4 } from 'uuid';
import { applyBlockTagBinding } from 'app/slate/extensions/tag/utils/blockTagging';
import { collectChainCarriers } from 'app/slate/extensions/tag/utils/blockChain';
import { selectionBlockCarrier } from './blockCarrier';
import { TagContext } from 'app/state/contexts/TagContext';
import { getTagType, isFractionConceptType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { findChainInsertionPoint } from './tagChainUtils';
import { useApplyCellTags } from './useApplyCellTags';
import { useApplyBlockChain } from './useApplyBlockChain';

interface UseTaggingParams {
  selectedTag: TaxonomyChild | null;
  selectedLinkroleUri?: string | null;
  isContentMarked: boolean;
  documentTags: TagWithMetadata[];
}

export const useTagging = ({ selectedTag, selectedLinkroleUri, isContentMarked, documentTags }: UseTaggingParams) => {
  const { mutate: createTag } = useCreateTag();
  const { mutate: continueTag } = useContinueTag();
  const { mutate: updateTag } = useUpdateTag();
  const { pDocument, activeSection, section, locale } = useEditorSelector((editor: any) => editor);
  const { data: conceptData } = useConcept(selectedTag?.id ?? null, pDocument?.superId);
  const { tableSelection, draftTag, blockSelection, blockScope, clearBlockSelection } = useContext(TagContext);
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const taxonomyId = config?.taxonomySelections?.find((s) => s.isActive)?.taxonomy?.id ?? null;
  const editor = useSlate();
  const applyCellTags = useApplyCellTags();
  const applyBlockChain = useApplyBlockChain();
  const { mutate: createFootnote } = useCreateFootnote();

  // Continuation modal state
  const [showContinuationModal, setShowContinuationModal] = useState(false);
  const [pendingBlockChain, setPendingBlockChain] = useState(false);
  const [pendingChainMulti, setPendingChainMulti] = useState(false);

  const selectionElementId = useMemo(() => {
    return getSelectionElementId(editor) ?? getSelectionElementId({ ...editor, selection: (editor as any).lastSelection }) ?? draftTag?.onId ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, editor.selection, (editor as any).lastSelection, draftTag?.onId]);

  const isTableTagging = !!tableSelection?.cells?.length;

  const isProseCarrier = blockSelection?.kind === 'paragraph' || blockSelection?.kind === 'list';
  const proseScope = isEscapedConceptType(selectedTag?.conceptType as any) ? 'block' : 'text';
  const effectiveScope = isProseCarrier ? proseScope : blockScope;

  const isImageBlockTagging = blockSelection?.kind === 'image';
  const isProseBlockTagging = isProseCarrier && proseScope === 'block';
  const isTableBlockTagging = !!tableSelection?.element && blockScope === 'table';
  const isBlockTagging = isImageBlockTagging || isProseBlockTagging || isTableBlockTagging;

  // Determine if content is marked (either text selection or table selection)
  const hasContentMarked = isContentMarked || isTableTagging || isBlockTagging;

  // Find existing tags with the same concept in the document
  const existingTagsWithSameConcept = useMemo(() => {
    if (!selectedTag || !documentTags) return [];
    return documentTags.filter((t) => t.concept?.name === selectedTag.id);
  }, [selectedTag, documentTags]);

  const conceptExistsInDocument = existingTagsWithSameConcept.length > 0;

  const handleTableTagging = async () => {
    if (!tableSelection?.cells?.length || !selectedTag || !conceptData?.id) return;
    await applyCellTags({
      mode: 'create',
      cells: tableSelection.cells,
      referenceDataSuperId: tableSelection.referenceDataSuperId,
      conceptId: conceptData.id,
      linkroleUri: selectedLinkroleUri ?? null,
      type: getTagType(selectedTag.conceptType.baseXsdType ?? (selectedTag.conceptType as any).baseXbrliType),
      taxonomyId: taxonomyId ?? '',
      sectionContext: {
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
      },
      locale,
      onTagsCreated: tableSelection.onTagsCreated,
      blockedTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
    });
  };

  const createNormalTag = useCallback(() => {
    if (!selectedTag) return;
    const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;

    createAndInsertTag({
      editor,
      tagType: selectedTag.id,
      tagData: {
        taxonomyId: taxonomyId ?? '',
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
        onId: selectionElementId,
        conceptId: conceptData?.id ?? '',
        lang: locale,
        type: getTagType(selectedTag.conceptType.baseXsdType ?? (selectedTag.conceptType as any).baseXbrliType),
        value: selectedText || undefined,
        linkroleUri: selectedLinkroleUri ?? null,
      },
      continuationTag: null,
      createTagMutation: createTag,
      continueTagMutation: continueTag,
      updateTagMutation: updateTag,
      callbacks: {},
    });
  }, [
    selectedTag,
    selectedLinkroleUri,
    editor,
    taxonomyId,
    pDocument,
    section,
    activeSection,
    selectionElementId,
    conceptData,
    locale,
    createTag,
    continueTag,
    updateTag,
  ]);

  const createContinuationTag = useCallback(
    (continuationSource: { tagId: string; tagType: string } | null, successor?: { tagId: string } | null) => {
      if (!selectedTag) return;
      // Must have either a predecessor or a successor to form a continuation
      if (!continuationSource && !successor) return;
      const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;

      createAndInsertTag({
        editor,
        tagType: selectedTag.id,
        tagData: {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          onId: selectionElementId,
          conceptId: conceptData?.id ?? '',
          lang: locale,
          type: getTagType(selectedTag.conceptType.baseXsdType ?? (selectedTag.conceptType as any).baseXbrliType),
          value: selectedText || undefined,
          linkroleUri: selectedLinkroleUri ?? null,
        },
        continuationTag: continuationSource,
        successorTag: successor || null,
        createTagMutation: createTag,
        continueTagMutation: continueTag,
        updateTagMutation: updateTag,
        callbacks: {},
      });
    },
    [
      selectedTag,
      selectedLinkroleUri,
      editor,
      taxonomyId,
      pDocument,
      section,
      activeSection,
      selectionElementId,
      conceptData,
      locale,
      createTag,
      continueTag,
      updateTag,
    ]
  );

  const handleTextTagging = () => {
    if (!selectedTag) return;

    if (conceptExistsInDocument) {
      setShowContinuationModal(true);
      return;
    }

    createNormalTag();
  };

  const blockCarrier = useMemo((): { path: any; elementId: string } | null => {
    const fromSelection = selectionBlockCarrier(blockSelection as any, effectiveScope);
    if (fromSelection) return fromSelection;
    if (isTableBlockTagging && tableSelection?.element) {
      const el = tableSelection.element as any;
      if (!el.uuid) return null;
      try {
        const ReactEditorLazy = require('slate-react').ReactEditor;
        return { path: ReactEditorLazy.findPath(editor, el), elementId: el.uuid };
      } catch {
        return null;
      }
    }
    return null;
  }, [blockSelection, effectiveScope, isTableBlockTagging, tableSelection, editor]);

  const handleExcludeContent = useCallback(() => {
    insertExclude(editor);
  }, [editor]);

  // Mark the selected report text as a footnote: wrap in Slate, persist the row.
  const handleCreateFootnote = useCallback(() => {
    const footnoteId = uuidv4();
    insertFootnote(editor, footnoteId);
    createFootnote({ id: footnoteId, documentSuperId: pDocument.superId });
  }, [editor, createFootnote, pDocument]);

  // Unplaced hidden fact: no document location — the value is entered in Tagginfo
  // and the fact emits inside <ix:hidden>.
  const handleHiddenTagging = useCallback(
    (onCreated?: (tag: unknown) => void) => {
      if (!selectedTag || !conceptData?.id) return;
      createTag(
        {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          conceptId: conceptData.id,
          lang: locale,
          type: isFractionConceptType(selectedTag.conceptType)
            ? 'fraction'
            : getTagType(selectedTag.conceptType?.baseXsdType ?? (selectedTag.conceptType as any)?.baseXbrliType),
          hidden: true,
          linkroleUri: selectedLinkroleUri ?? null,
        } as any,
        {
          onSuccess: (created: any) => onCreated?.(created),
        }
      );
    },
    [selectedTag, selectedLinkroleUri, conceptData, createTag, taxonomyId, pDocument, locale]
  );

  const createNormalBlockTag = useCallback(() => {
    if (!selectedTag || !conceptData?.id || !blockCarrier) return;
    const { path, elementId } = blockCarrier;
    createTag(
      {
        taxonomyId: taxonomyId ?? '',
        documentSuperId: pDocument.superId,
        sectionSuperId: section.superId,
        pageId: activeSection.uuid,
        onId: elementId,
        conceptId: conceptData.id,
        lang: locale,
        type: 'nonNumeric',
        linkroleUri: selectedLinkroleUri ?? null,
      } as any,
      {
        onSuccess: (created: any) => {
          if (created?.id) applyBlockTagBinding(editor, path, { tagId: created.id });
          if (isImageBlockTagging || isProseBlockTagging) clearBlockSelection?.();
        },
      }
    );
  }, [
    selectedTag,
    selectedLinkroleUri,
    conceptData,
    blockCarrier,
    createTag,
    taxonomyId,
    pDocument,
    section,
    activeSection,
    locale,
    editor,
    isImageBlockTagging,
    isProseBlockTagging,
    clearBlockSelection,
  ]);

  const createBlockContinuationTag = useCallback(
    (predecessor: { tagId: string } | null, successor: { tagId: string } | null) => {
      if (!selectedTag || !conceptData?.id || !blockCarrier) return;
      if (!predecessor && !successor) return;
      const { path, elementId } = blockCarrier;
      continueTag(
        {
          taxonomyId: taxonomyId ?? '',
          documentSuperId: pDocument.superId,
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          onId: elementId,
          conceptId: conceptData.id,
          lang: locale,
          type: 'nonNumeric',
          linkroleUri: selectedLinkroleUri ?? null,
          predecessorId: predecessor?.tagId,
          successorId: successor?.tagId,
        } as any,
        {
          onSuccess: (created: any) => {
            if (created?.id) {
              applyBlockTagBinding(editor, path, {
                tagId: created.id,
                continuedAt: successor?.tagId,
              });
            }
            if (isImageBlockTagging || isProseBlockTagging) clearBlockSelection?.();
          },
        }
      );
    },
    [
      selectedTag,
      selectedLinkroleUri,
      conceptData,
      blockCarrier,
      continueTag,
      taxonomyId,
      pDocument,
      section,
      activeSection,
      locale,
      editor,
      isImageBlockTagging,
      isProseBlockTagging,
      clearBlockSelection,
    ]
  );

  const runBlockChain = useCallback(
    (predecessorId?: string, successorId?: string) => {
      if (!conceptData?.id) return;
      const range = (editor as any).lastSelection ?? editor.selection;
      if (!range) return;
      void applyBlockChain({
        editor,
        range,
        base: {
          documentSuperId: pDocument.superId,
          taxonomyId: taxonomyId ?? '',
          sectionSuperId: section.superId,
          pageId: activeSection.uuid,
          conceptId: conceptData.id,
          lang: locale,
          linkroleUri: selectedLinkroleUri ?? null,
        },
        conceptTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
        predecessorId,
        successorId,
      }).then(() => clearBlockSelection?.());
    },
    [
      applyBlockChain,
      conceptData,
      editor,
      pDocument,
      section,
      activeSection,
      locale,
      taxonomyId,
      selectedLinkroleUri,
      existingTagsWithSameConcept,
      clearBlockSelection,
    ]
  );

  const handleBlockTagging = useCallback(() => {
    if (!selectedTag || !conceptData?.id) return;
    if (isProseBlockTagging) {
      const range = (editor as any).lastSelection ?? editor.selection;
      const spansMultiple = !!range && collectChainCarriers(editor, range).length >= 2;
      if (spansMultiple) {
        if (conceptExistsInDocument) {
          setPendingChainMulti(true);
          setShowContinuationModal(true);
          return;
        }
        runBlockChain();
        return;
      }
    }
    // Single carrier / image / table (unchanged).
    if (!blockCarrier) return;
    if (conceptExistsInDocument) {
      setPendingBlockChain(true);
      setShowContinuationModal(true);
      return;
    }
    createNormalBlockTag();
  }, [selectedTag, conceptData, blockCarrier, conceptExistsInDocument, createNormalBlockTag, isProseBlockTagging, editor, runBlockChain]);

  const handleContinuationConfirm = useCallback(() => {
    setShowContinuationModal(false);

    if (!selectedTag || existingTagsWithSameConcept.length === 0) {
      setPendingBlockChain(false);
      return;
    }

    const sections = pDocument?.content?.sections ?? [];
    const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections);

    if (pendingChainMulti) {
      setPendingChainMulti(false);
      runBlockChain(predecessor?.id, successor?.id);
      return;
    }

    if (pendingBlockChain) {
      setPendingBlockChain(false);
      if (predecessor) {
        createBlockContinuationTag({ tagId: predecessor.id }, successor ? { tagId: successor.id } : null);
      } else if (successor) {
        createBlockContinuationTag(null, { tagId: successor.id });
      }
      return;
    }

    if (predecessor) {
      createContinuationTag({ tagId: predecessor.id, tagType: predecessor.concept?.name ?? selectedTag.id }, successor ? { tagId: successor.id } : null);
    } else if (successor) {
      createContinuationTag(null, { tagId: successor.id });
    }
  }, [
    selectedTag,
    existingTagsWithSameConcept,
    editor,
    pDocument,
    section,
    createContinuationTag,
    pendingBlockChain,
    createBlockContinuationTag,
    pendingChainMulti,
    runBlockChain,
  ]);

  const handleContinuationCancel = useCallback(() => {
    setShowContinuationModal(false);
    setPendingBlockChain(false);
    setPendingChainMulti(false);
  }, []);

  const handleTagging = isBlockTagging ? handleBlockTagging : isTableTagging ? handleTableTagging : handleTextTagging;

  return {
    handleTagging,
    handleHiddenTagging,
    handleExcludeContent,
    handleCreateFootnote,
    isTableTagging,
    isBlockTagging,
    hasContentMarked,
    showContinuationModal,
    handleContinuationConfirm,
    handleContinuationCancel,
  };
};
